import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import { VIEW_KEY_BY_REPORT_KEY } from "./reportColumnCatalog";
import {
  formatDateOnly,
  formatTimestampUtc,
} from "../../../utils/dataExchangeDates";

/* ============================================================
   Date-only columns — must match backend/reportFormatting.js
   ============================================================ */
const DATE_ONLY_COLUMNS = new Set([
  "contact_date",
  "next_contact",
  "last_contact",
  "demo_date",
]);

/**
 * Coerce a value to YYYY-MM-DD string. Mirrors the backend helper.
 * Returns "" for null/undefined/invalid.
 */

/**
 * Render a single cell value for the preview table.
 *
 * - null / undefined  → ""
 * - arrays            → "A, B" (comma-separated, nulls dropped)
 * - objects           → JSON string (last resort)
 * - everything else   → String(v)
 */
function renderCell(v) {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) {
    return v
      .map((x) => (x === null || x === undefined ? "" : String(x).trim()))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/**
 * Parse a value that may be a JSON array string, a CSV string, or an
 * already-parsed array. Returns an array of trimmed strings.
 */
function asArray(v) {
  if (v === null || v === undefined) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        /* fall through */
      }
    }
  }
  return [];
}

/**
 * Trim a multi-value cell value by the given allowed list.
 * If no filter list is provided → returns the value unchanged.
 */
function trimMultiValue(value, allowedList) {
  if (!Array.isArray(allowedList) || allowedList.length === 0) return value;
  const allowed = new Set(allowedList.map((x) => String(x).trim()));
  const arr = asArray(value);
  if (arr.length === 0) return value;
  return arr.filter((x) => allowed.has(String(x).trim()));
}

export default function ReportTemplatePreview({
  payload,
  filters,
  title,
  headerText,
}) {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.previewReportTemplate({
          payload,
          filters,
          limit: 50,
        });
        if (cancelled) return;
        setColumns(Array.isArray(res?.columns) ? res.columns : []);
        setRows(Array.isArray(res?.rows) ? res.rows : []);
      } catch {
        if (!cancelled) {
          setColumns([]);
          setRows([]);
          setError("Failed to load preview");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(payload), JSON.stringify(filters)]);

  const labels = payload?.columnLabels || {};
  const displayLabel = (c) => {
    const vk = VIEW_KEY_BY_REPORT_KEY[c.key];
    return (vk && labels[vk]) || c.label;
  };

  /* Client-side safety net: honor multiValueColumnMode even if the
     backend was not given the mode (older server version, etc). */
  const mode = String(filters?.multiValueColumnMode || "all").toLowerCase();
  const isFilteredMode = mode === "filtered";
  const productFilter = Array.isArray(filters?.products)
    ? filters.products
    : [];
  const dmsFilter = Array.isArray(filters?.dmsProviders)
    ? filters.dmsProviders
    : [];

  const displayCellValue = (row, columnKey) => {
    const raw = row[columnKey];

    // Use the shared date-formatting helper for date-only fields.
    if (DATE_ONLY_COLUMNS.has(columnKey)) {
      return formatDateOnly(raw);
    }

    // Use the shared helper for genuine timestamps.
    if (["created_at", "updated_at"].includes(columnKey)) {
      return formatTimestampUtc(raw);
    }

    /* Multi-value columns — apply filter trim in filtered mode */
    if (!isFilteredMode) return renderCell(raw);

    if (columnKey === "products" && productFilter.length > 0) {
      return renderCell(trimMultiValue(raw, productFilter));
    }

    if (columnKey === "dms_providers" && dmsFilter.length > 0) {
      return renderCell(trimMultiValue(raw, dmsFilter));
    }

    return renderCell(raw);
  };

  return (
    <div className="r6-tpl-preview">
      <div className="r6-tpl-preview-head">
        <i className="fas fa-eye" />
        <span>Live preview</span>
        <span className="r6-tpl-preview-count">
          {loading ? "…" : `${rows.length} row${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {error && (
        <div className="error-message" role="alert">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {(title || headerText) && (
        <div className="r6-tpl-preview-banner">
          {title && <div className="r6-tpl-preview-title">{title}</div>}
          {headerText && <div className="r6-tpl-preview-sub">{headerText}</div>}
        </div>
      )}

      {loading ? (
        <div className="r6-tpl-preview-empty">Loading…</div>
      ) : columns.length === 0 ? (
        <div className="r6-tpl-preview-empty">No columns selected.</div>
      ) : (
        <div className="r6-tpl-preview-table-wrap">
          <table className="r6-tpl-preview-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{displayLabel(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="r6-tpl-preview-empty-cell"
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.key}>{displayCellValue(r, c.key)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
